import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Descriptions,
  Flex,
  Form,
  Radio,
  Select,
  Spin,
  Switch
} from 'antd';
import { BaseButton } from 'components/button/button-base';
import { ErrorDisplay } from 'components/ErrorDisplay';
import { Text } from 'components/mvp-typography';
import { add, Duration, formatISO, getUnixTime } from 'date-fns';
import { APIPatient } from 'lib/api/firebase/apis/api-patient';
import { AssessmentType } from 'lib/documents/patient-assessment/patient-assessment';
import { useAssessmentAPI } from 'lib/hooks/tanstack/use-assessmentAPI';
import { useClinicAssessmentBalance } from 'lib/hooks/tanstack/use-clinicAssessmentBalance';
import { useGetPatientImpactProfile } from 'lib/hooks/tanstack/use-getPatientImpactProfile';
import { useCurrentClinicHasPaymentMethod } from 'lib/hooks/tanstack/use-getProductPrice';
import { useLoginLinkAPI } from 'lib/hooks/tanstack/use-loginLink';
import { useQueryErrorNotifier } from 'lib/hooks/use-query-error-notifier';
import { useClinicianContext } from 'lib/hooks/use-resolveCurrentClinicInfo';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { useErrorListContext } from 'lib/utils/useErrorList';
import { RefreshCcw } from 'lucide-react';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { paths } from 'routes';
import { theme } from 'styles/colors';
import { AssessmentBalanceAlerts } from './assessment-balance-alerts';
import { ConfirmOnDemandPurchaseModal } from './confirm-on-demand-purchase-modal';

export interface CreateAssessmentFormValues {
  assessmentType: AssessmentType;
  expiration: string;
  importLastImpactTestOnStart: boolean;
}

interface CreateAssessmentLinkFormProps {
  patientId: string;
  onSuccess?: (args: { expiresAt: number }) => void;
}

const stringifyDuration = (duration: Duration) => JSON.stringify(duration);

function useGetPatientLatestImpactTest(
  patientId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['temp', patientId],
    queryFn: () => APIPatient.getPatientLatestImpactTest({ patientId }),
    enabled: options?.enabled
  });
}

export function CreateAssessmentForm(props: CreateAssessmentLinkFormProps) {
  const [form] = Form.useForm<CreateAssessmentFormValues>();
  const mutationActions = useLoginLinkAPI();
  const assessmentMutationActions = useAssessmentAPI();
  const clinicianContext = useClinicianContext();

  const formAssessmentType = Form.useWatch('assessmentType', form);
  const formUseImpactTest = Form.useWatch('importLastImpactTestOnStart', form);
  const balance = useClinicAssessmentBalance(formAssessmentType);
  const impactProfile = useGetPatientImpactProfile(props.patientId);

  const passportIdExists = !!impactProfile.data?.data.passportId;

  const latestImpactTestQuery = useGetPatientLatestImpactTest(props.patientId, {
    enabled: passportIdExists
  });
  const history = useHistory();
  const errorList = useErrorListContext();

  const [showOnDemandPurchaseModal, setShowOnDemandPurchaseModal] =
    useState(false);

  useQueryErrorNotifier(balance, errorList.errorList, 'Balance');
  useQueryErrorNotifier(impactProfile, errorList.errorList, 'ImpactProfile');

  const isInfiniteBalance = balance.data?.data.isInfinite === true;
  const emptyBalance = balance.data?.data.available === 0 && !isInfiniteBalance;
  const emptyBalanceAndBlocked =
    emptyBalance &&
    balance.data?.data.supportsOnDemandPurchases.value === false;

  const hasPaymentMethodQuery = useCurrentClinicHasPaymentMethod();

  async function onFinish(args: CreateAssessmentFormValues) {
    if (emptyBalance) {
      setShowOnDemandPurchaseModal(true);
      return;
    }
    const expiresAt = getUnixTime(add(new Date(), JSON.parse(args.expiration)));

    await assessmentMutationActions.create.mutateAsync(
      {
        assessmentType: args.assessmentType,
        patientId: props.patientId,
        clinicId: clinicianContext.currentClinic,
        importLastImpactTestOnStart: args.importLastImpactTestOnStart,
        expiresAt: expiresAt,
        skipExpiryCheck: true
      },
      {
        onError: error =>
          errorList.errorList.addError(resolveError(error), 'create-actionlink')
      }
    );

    props.onSuccess?.({ expiresAt });
  }

  const handleReviewPurchase = () => {
    history.push({
      pathname: `${paths.clinician.billing.buy.ppu()}`,
      search: new URLSearchParams({
        assessmentType: formAssessmentType,
        returnTo: location.pathname
      }).toString()
    });
    handleCloseModal();
  };

  const handleCloseModal = () => {
    setShowOnDemandPurchaseModal(false);
  };
  const hasPaymentMethod =
    hasPaymentMethodQuery.data?.data.hasPaymentMethod === true;

  // Check if the last Impact test type does not match the assessment type when importing
  const isTestTypeMismatch =
    formUseImpactTest &&
    latestImpactTestQuery.data?.data.crx_mappedTestType !== formAssessmentType;

  if (impactProfile.isLoading) {
    return <Spin />;
  }
  const isLoading = balance.isLoading || impactProfile.isLoading;
  const isErrorState =
    balance.isError ||
    (emptyBalanceAndBlocked && !hasPaymentMethod) ||
    isTestTypeMismatch;

  return (
    <>
      <Form
        form={form}
        layout="horizontal"
        onFinish={onFinish}
        style={{ width: '100%', maxWidth: 600 }}
        initialValues={{
          importLastImpactTestOnStart: false
        }}
      >
        <Flex vertical gap={16}>
          <Form.Item
            label="Select assessment type"
            name="assessmentType"
            rules={[
              { required: true, message: 'Please select an assessment type.' }
            ]}
          >
            <Select style={{ width: '100%' }}>
              <Select.Option value="baseline">Baseline test</Select.Option>
              <Select.Option value="post-injury">
                Post-Injury test
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Set expiration"
            name="expiration"
            rules={[
              { required: true, message: 'Please select an expiration.' }
            ]}
            tooltip={
              'Set the expiration of this assessment. The test taker will have until this time to start the assessment.'
            }
          >
            <Radio.Group
              style={{ width: '100%' }}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value={stringifyDuration({ days: 3 })}>
                3 Days
              </Radio.Button>
              <Radio.Button value={stringifyDuration({ days: 7 })}>
                7 Days
              </Radio.Button>
              <Radio.Button value={stringifyDuration({ days: 30 })}>
                30 Days
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Flex vertical>
            <Form.Item
              label="Use the most-recent completed ImPACT test"
              name="importLastImpactTestOnStart"
              rules={[{ required: true }]}
              tooltip="When enabled, the test-taker's most recent ImPACT test data will be automatically imported when they start this assessment."
            >
              <Switch disabled={!passportIdExists} />
            </Form.Item>
            {formUseImpactTest && (
              <LastImpactTestDisplay patientId={props.patientId} />
            )}
            {/* {!passportIdExists && (
              <Alert
                type="warning"
                showIcon

                message={<Text>No ImPACT history detected.</Text>}
                description={
                  <Text>
                    This option will be enabled after the test-taker completes
                    their first assessment.
                  </Text>
                }
              />
            )} */}
          </Flex>

          {isTestTypeMismatch && latestImpactTestQuery.isFetched && (
            <Alert
              type="error"
              showIcon
              message={
                <Text>
                  The last ImPACT test type does not match the selected
                  assessment type.
                </Text>
              }
              description={
                <Text>
                  Please change the assessment type, or toggle off the option to
                  use the most-recent ImPACT test.
                </Text>
              }
            />
          )}

          <AssessmentBalanceAlerts assessmentType={formAssessmentType} />
          <Flex vertical gap={16} align="center" style={{ width: '100%' }}>
            <div>
              <BaseButton
                loading={
                  assessmentMutationActions.create.isLoading ||
                  mutationActions.create.isLoading
                }
                htmlType="submit"
                disabled={isLoading || isErrorState}
              >
                Create new assessment
              </BaseButton>
            </div>
          </Flex>
        </Flex>
      </Form>

      <ConfirmOnDemandPurchaseModal
        showModal={showOnDemandPurchaseModal}
        onClose={handleCloseModal}
        onReviewPurchase={handleReviewPurchase}
        assessmentType={formAssessmentType}
      />
    </>
  );
}

function LastImpactTestDisplay(props: { patientId: string }) {
  const latestImpactTestQuery = useGetPatientLatestImpactTest(props.patientId);

  if (latestImpactTestQuery.isLoading) {
    return (
      <div>
        <Text>Fetching the test-takers latest ImPACT test details</Text>
        <Spin />
      </div>
    );
  }

  if (latestImpactTestQuery.isError) {
    return (
      <div>
        <ErrorDisplay
          error={resolveError(latestImpactTestQuery.error)}
          title="Failed to fetch the test-takers latest ImPACT test details"
          description={
            <div>
              <Text>This error has been logged</Text>
              <BaseButton
                type="dashed"
                loading={latestImpactTestQuery.isFetching}
                onClick={() => latestImpactTestQuery.refetch()}
              >
                Retry
              </BaseButton>
            </div>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <BaseButton
        icon={<RefreshCcw />}
        loading={latestImpactTestQuery.isFetching}
        onClick={() => latestImpactTestQuery.refetch()}
      >
        Refetch
      </BaseButton>
      <Text color={theme.grey.medium}>
        <small>
          Fetched at {formatISO(latestImpactTestQuery.dataUpdatedAt)}
        </small>
      </Text>
      <Descriptions column={{ xxl: 3, xl: 2 }} size="small">
        <Descriptions.Item label="ImPACT Test ID">
          {latestImpactTestQuery.data.data.testID}
        </Descriptions.Item>
        <Descriptions.Item label="Date">
          {latestImpactTestQuery.data.data.currentDate}
        </Descriptions.Item>
        <Descriptions.Item label="Test Type">
          {latestImpactTestQuery.data.data.crx_mappedTestType}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
}
